import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaImage, FaTrash, FaUpload, FaUndo, FaEyeDropper } from 'react-icons/fa';
import config from '../config/config';
import { useToast } from './ToastContext';
import LogoEnquadrado from './LogoEnquadrado';
import { TIPOS_LOGO, LOGO_MAXIMO_MB, carregarLogoEmpresa, salvarAjusteLogo } from '../others/logoEmpresa';
import { AJUSTE_PADRAO, ZOOM_MIN, ZOOM_MAX, normalizarAjuste, corDaBorda } from '../others/logoAjuste';

// Configurações -> Logo da empresa (só gerente/manager): envia, troca ou remove o logo que aparece no cartão
// central da página inicial (o do topo é sempre o do sistema) e ajusta como ele ocupa o cartão: inteiro ou
// preenchendo, tamanho, posição e cor de fundo. A prévia usa o mesmo componente da página inicial.
const CardLogoEmpresa = () => {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const [logo, setLogo] = useState(null); // { temLogo, url, ajuste } como está salvo
  const [ajuste, setAjuste] = useState(AJUSTE_PADRAO); // em edição
  const [salvando, setSalvando] = useState(false);
  const entrada = useRef(null);
  const urlAtual = useRef(null); // objectURL da imagem mostrada, liberado ao trocar ou sair

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

  const trocarUrl = (url) => {
    if (urlAtual.current) URL.revokeObjectURL(urlAtual.current);
    urlAtual.current = url;
  };

  const recarregar = async () => {
    try {
      const novo = await carregarLogoEmpresa();
      trocarUrl(novo.url);
      setLogo(novo);
      setAjuste(novo.ajuste);
    } catch (error) {
      trocarUrl(null);
      setLogo({ temLogo: false, url: null, ajuste: AJUSTE_PADRAO });
      setAjuste(AJUSTE_PADRAO);
    }
  };

  useEffect(() => {
    recarregar();
    return () => trocarUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mensagem = (error, padrao) => error?.response?.data?.message || padrao;

  const enviar = async (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    if (!TIPOS_LOGO.includes(arquivo.type)) { addToast('warning', 'O logo precisa ser uma imagem PNG ou JPG.'); return; }
    if (arquivo.size > LOGO_MAXIMO_MB * 1024 * 1024) { addToast('warning', `O logo passa de ${LOGO_MAXIMO_MB} MB. Use uma imagem menor.`); return; }
    setSalvando(true);
    try {
      const dados = new FormData();
      dados.append('file', arquivo);
      await axios.post(`${API_BASE_URL}/empresas/minha/logo`, dados, auth());
      await recarregar();
      addToast('success', 'Logo atualizado! Ajuste abaixo como ele ocupa o cartão da página inicial.');
    } catch (error) {
      addToast('error', mensagem(error, 'Não foi possível enviar o logo'));
    } finally {
      setSalvando(false);
    }
  };

  const remover = async () => {
    setSalvando(true);
    try {
      await axios.delete(`${API_BASE_URL}/empresas/minha/logo`, auth());
      await recarregar();
      addToast('success', 'Logo removido. A página inicial volta a mostrar o logo do sistema.');
    } catch (error) {
      addToast('error', mensagem(error, 'Não foi possível remover o logo'));
    } finally {
      setSalvando(false);
    }
  };

  const salvarAjuste = async () => {
    setSalvando(true);
    try {
      const salvo = await salvarAjusteLogo(ajuste);
      setLogo((l) => ({ ...l, ajuste: salvo }));
      setAjuste(salvo);
      addToast('success', 'Enquadramento salvo! Já vale na página inicial.');
    } catch (error) {
      addToast('error', mensagem(error, 'Não foi possível salvar o enquadramento'));
    } finally {
      setSalvando(false);
    }
  };

  // Cor da moldura do logo, para o cartão continuar o fundo dele (logo com fundo colorido)
  const usarCorDaBorda = () => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const cor = corDaBorda(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height);
      if (cor) mudar({ corFundo: cor });
      else addToast('info', 'A borda do logo é transparente: escolha a cor do fundo à mão.');
    };
    img.src = logo.url;
  };

  const mudar = (campos) => setAjuste((a) => normalizarAjuste({ ...a, ...campos }));

  if (!logo) return null;
  const alterado = JSON.stringify(ajuste) !== JSON.stringify(logo.ajuste);

  return (
    <div style={styles.card}>
      <h3 style={styles.titulo}>Logo da empresa</h3>
      <p style={styles.descricao}>
        O logo da sua empresa aparece no cartão central da página inicial, no lugar do logo do sistema. PNG ou JPG,
        até {LOGO_MAXIMO_MB} MB.
      </p>

      <div style={styles.linha}>
        {!logo.temLogo && (
          <div style={styles.atual}>
            <span style={styles.semLogo}><FaImage size={22} /><br />Sem logo</span>
          </div>
        )}
        <div style={styles.botoes}>
          <input ref={entrada} type="file" accept="image/png,image/jpeg" onChange={enviar} style={{ display: 'none' }} />
          <button type="button" style={styles.botao} disabled={salvando} onClick={() => entrada.current?.click()}>
            <FaUpload style={{ marginRight: 8 }} /> {salvando ? 'Salvando...' : (logo.temLogo ? 'Trocar logo' : 'Enviar logo')}
          </button>
          {logo.temLogo && (
            <button type="button" style={styles.botaoRemover} disabled={salvando} onClick={remover}>
              <FaTrash style={{ marginRight: 8 }} /> Remover
            </button>
          )}
        </div>
      </div>

      {logo.temLogo && logo.url && (
        <div style={styles.editor}>
          {/* Prévia no formato do cartão da página inicial */}
          <div>
            <div style={styles.previa}>
              <LogoEnquadrado url={logo.url} ajuste={ajuste} alt="Prévia do logo" />
            </div>
            <p style={styles.legenda}>Prévia do cartão da página inicial</p>
          </div>

          <div style={styles.controles}>
            <div style={styles.grupo}>
              <span style={styles.rotulo}>Como o logo ocupa o cartão</span>
              <div style={styles.alternar}>
                {[['CONTER', 'Logo inteiro'], ['PREENCHER', 'Preencher o cartão']].map(([valor, texto]) => (
                  <button key={valor} type="button" onClick={() => mudar({ modo: valor })}
                    style={{ ...styles.opcao, ...(ajuste.modo === valor ? styles.opcaoAtiva : {}) }}>
                    {texto}
                  </button>
                ))}
              </div>
              <span style={styles.dica}>
                {ajuste.modo === 'CONTER'
                  ? 'Mostra o logo todo; a sobra fica com a cor de fundo.'
                  : 'Cobre o cartão inteiro; o que passar das bordas é cortado (use a posição para escolher o corte).'}
              </span>
            </div>

            <label style={styles.grupo}>
              <span style={styles.rotulo}>Tamanho <strong>{ajuste.zoom}%</strong></span>
              <input type="range" min={ZOOM_MIN} max={ZOOM_MAX} step={5} value={ajuste.zoom}
                onChange={(e) => mudar({ zoom: e.target.value })} />
            </label>

            <label style={styles.grupo}>
              <span style={styles.rotulo}>Posição horizontal <small>(esquerda ↔ direita)</small></span>
              <input type="range" min={0} max={100} value={ajuste.posX} onChange={(e) => mudar({ posX: e.target.value })} />
            </label>

            <label style={styles.grupo}>
              <span style={styles.rotulo}>Posição vertical <small>(cima ↕ baixo)</small></span>
              <input type="range" min={0} max={100} value={ajuste.posY} onChange={(e) => mudar({ posY: e.target.value })} />
            </label>

            <div style={styles.grupo}>
              <span style={styles.rotulo}>Cor de fundo do cartão</span>
              <div style={styles.cores}>
                <input type="color" value={ajuste.corFundo.toLowerCase()} onChange={(e) => mudar({ corFundo: e.target.value })}
                  style={styles.seletorCor} aria-label="Cor de fundo do cartão" />
                <button type="button" style={styles.opcao} onClick={() => mudar({ corFundo: '#FFFFFF' })}>Branco</button>
                <button type="button" style={styles.opcao} onClick={usarCorDaBorda} title="Usa a cor da borda do logo, para o fundo do logo continuar pelo cartão">
                  <FaEyeDropper style={{ marginRight: 6 }} /> Cor da borda do logo
                </button>
              </div>
            </div>

            <div style={styles.acoes}>
              <button type="button" style={alterado ? styles.botao : { ...styles.botao, ...styles.desabilitado }}
                disabled={!alterado || salvando} onClick={salvarAjuste}>
                {salvando ? 'Salvando...' : 'Salvar enquadramento'}
              </button>
              <button type="button" style={styles.opcao} disabled={!alterado} onClick={() => setAjuste(logo.ajuste)}>
                <FaUndo style={{ marginRight: 6 }} /> Desfazer
              </button>
              <button type="button" style={styles.opcao} onClick={() => setAjuste(AJUSTE_PADRAO)}>Voltar ao padrão</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VERDE = '#008080';
const styles = {
  card: { backgroundColor: '#fff', borderRadius: '0.5rem', padding: '1.5rem', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)', marginBottom: '1.5rem' },
  titulo: { fontSize: '1.125rem', fontWeight: 600, color: '#2d3748', margin: '0 0 0.5rem 0' },
  descricao: { fontSize: '0.875rem', color: '#718096', margin: '0 0 1rem 0' },
  linha: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  atual: { width: 200, height: 90, border: '1px dashed #cbd5e0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7fafc' },
  semLogo: { color: '#a0aec0', fontSize: 13, textAlign: 'center' },
  botoes: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  botao: { display: 'inline-flex', alignItems: 'center', padding: '0.5rem 1rem', backgroundColor: VERDE, color: '#fff', fontSize: '0.875rem', fontWeight: 500, borderRadius: '0.375rem', border: 'none', cursor: 'pointer' },
  botaoRemover: { display: 'inline-flex', alignItems: 'center', padding: '0.5rem 1rem', backgroundColor: '#fff', color: '#c0392b', fontSize: '0.875rem', fontWeight: 500, borderRadius: '0.375rem', border: '1px solid #e8b4ae', cursor: 'pointer' },
  desabilitado: { backgroundColor: '#a0aec0', cursor: 'not-allowed' },
  editor: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginTop: 18, paddingTop: 16, borderTop: '1px solid #edf2f7' },
  // Mesmo visual do cartão da página inicial (borda, cantos e proporção larga)
  previa: { position: 'relative', overflow: 'hidden', width: '100%', aspectRatio: '16 / 9', borderRadius: 15, border: '1px solid rgba(0,128,128,0.2)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', background: '#fff' },
  legenda: { fontSize: 12, color: '#a0aec0', margin: '6px 0 0 0', textAlign: 'center' },
  controles: { display: 'flex', flexDirection: 'column', gap: 12 },
  grupo: { display: 'flex', flexDirection: 'column', gap: 5 },
  rotulo: { fontSize: 13, fontWeight: 600, color: '#2d3748' },
  dica: { fontSize: 12, color: '#718096' },
  alternar: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  opcao: { display: 'inline-flex', alignItems: 'center', padding: '5px 10px', background: '#fff', color: '#2d3748', fontSize: 13, border: '1px solid #cbd5e0', borderRadius: 6, cursor: 'pointer' },
  opcaoAtiva: { borderColor: VERDE, background: '#f0fdfa', color: '#004d4d', fontWeight: 600 },
  cores: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' },
  seletorCor: { width: 38, height: 32, padding: 2, border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff', cursor: 'pointer' },
  acoes: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 },
};

export default CardLogoEmpresa;
